import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchWarrantyClaimById, updateWarrantyClaim } from '@/services/warranties';
import { queryKeys } from '@/lib/queryKeys';
import { parseDatabaseError } from '@/lib/errors';
import { WarrantyClaim, WarrantyClaimStatus } from '@/types/models';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import {
  ShieldCheck,
  AlertTriangle,
  User,
  Save,
  Loader2,
} from 'lucide-react';

interface WarrantyClaimDetailProps {
  claimId: string;
  onClose: () => void;
}

const WARRANTY_STATUSES: Array<{ id: WarrantyClaimStatus; label: string }> = [
  { id: 'open', label: 'Open' },
  { id: 'inspection', label: 'Under Inspection' },
  { id: 'approved', label: 'Claim Approved' },
  { id: 'rejected', label: 'Claim Rejected' },
  { id: 'repairing', label: 'Repairing Under Warranty' },
  { id: 'replaced', label: 'Unit Replaced' },
  { id: 'closed', label: 'Closed' },
];

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-PK', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export const WarrantyClaimDetail: React.FC<WarrantyClaimDetailProps> = ({ claimId, onClose }) => {
  const queryClient = useQueryClient();

  const { data: claim, isLoading, error, refetch } = useQuery<WarrantyClaim>({
    queryKey: queryKeys.warranties.detail(claimId),
    queryFn: () => fetchWarrantyClaimById(claimId),
  });

  const [status, setStatus] = useState<WarrantyClaimStatus>('open');
  const [resolution, setResolution] = useState<string>('');
  const [updateError, setUpdateError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  const initEdit = (c: WarrantyClaim) => {
    setStatus(c.status);
    setResolution(c.resolution ?? '');
    setIsEditing(true);
  };

  const updateMutation = useMutation({
    mutationFn: async () => {
      return await updateWarrantyClaim(claimId, {
        status,
        resolution: resolution.trim() || null,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.warranties.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.warranties.detail(claimId) });
      setIsEditing(false);
    },
    onError: (err: unknown) => {
      setUpdateError(parseDatabaseError(err));
    },
  });

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    setUpdateError(null);
    updateMutation.mutate();
  };

  const isExpired = claim?.laptop?.warranty_expires
    ? new Date(claim.laptop.warranty_expires) < new Date(claim.opened_at)
    : false;

  return (
    <>
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-brand-green animate-spin" />
        </div>
      )}

      {error && <ErrorAlert message={parseDatabaseError(error)} onRetry={() => refetch()} />}

      {claim && !isLoading && (
        <div className="space-y-4 max-h-[75vh] overflow-y-auto pr-1 text-xs">
          {/* Header */}
          <div className="flex items-start justify-between pb-3 border-b border-brand-border">
            <div>
              <div className="flex items-center space-x-2">
                <h3 className="text-lg font-bold text-brand-black">CLAIM #{claim.claim_number}</h3>
                <span
                  className={`px-2 py-0.5 text-xs font-bold rounded-full uppercase ${
                    claim.status === 'approved' || claim.status === 'replaced'
                      ? 'bg-green-100 text-green-800'
                      : claim.status === 'rejected'
                      ? 'bg-red-100 text-red-800'
                      : 'bg-amber-100 text-amber-800'
                  }`}
                >
                  {claim.status}
                </span>
              </div>
              <p className="text-brand-muted mt-0.5">
                Opened: <strong>{formatDate(claim.opened_at)}</strong>
                {claim.closed_at && <span> • Closed: <strong>{formatDate(claim.closed_at)}</strong></span>}
              </p>
            </div>

            {!isEditing && (
              <button
                type="button"
                onClick={() => initEdit(claim)}
                className="px-3 py-1.5 bg-brand-green text-white font-semibold rounded hover:bg-brand-green-hover"
              >
                Update Claim Status
              </button>
            )}
          </div>

          {/* Warranty Validity Badge Banner */}
          <div
            className={`p-3 rounded-lg border flex items-center justify-between ${
              isExpired ? 'bg-red-50 border-red-200 text-red-900' : 'bg-green-50 border-green-200 text-green-900'
            }`}
          >
            <div className="flex items-center space-x-2 font-semibold">
              {isExpired ? <AlertTriangle className="w-4 h-4 text-red-600" /> : <ShieldCheck className="w-4 h-4 text-green-600" />}
              <span>
                Warranty Status at Claim Time:{' '}
                <strong>{isExpired ? 'WARRANTY EXPIRED' : 'ACTIVE COVERAGE'}</strong>
              </span>
            </div>
            {claim.laptop?.warranty_expires && (
              <span className="text-[11px]">Expires: {formatDate(claim.laptop.warranty_expires)}</span>
            )}
          </div>

          {/* Laptop & Customer Info */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="bg-brand-bg p-3 rounded-lg border border-brand-border space-y-1">
              <p className="font-bold text-brand-black uppercase">Laptop Item Details</p>
              <p className="font-bold text-brand-black text-sm">
                {claim.laptop?.brand} {claim.laptop?.model}
              </p>
              <p className="text-brand-muted">Stock ID: <strong className="text-brand-black">{claim.laptop?.stock_id}</strong></p>
              <p className="text-brand-muted">S/N: {claim.laptop?.serial_number || 'N/A'}</p>
            </div>

            <div className="bg-brand-bg p-3 rounded-lg border border-brand-border space-y-1">
              <p className="font-bold text-brand-black uppercase">Customer & Sale Invoice</p>
              {claim.customer ? (
                <div>
                  <p className="font-semibold text-brand-black flex items-center space-x-1">
                    <User className="w-3.5 h-3.5 text-brand-green shrink-0" />
                    <span>{claim.customer.name}</span>
                  </p>
                  {claim.customer.phone && <p className="text-brand-muted">Phone: {claim.customer.phone}</p>}
                </div>
              ) : (
                <p className="text-brand-muted italic">Customer record unavailable</p>
              )}
              {claim.sale && (
                <p className="text-brand-muted pt-1">
                  Invoice: <strong className="text-brand-black">#{claim.sale.invoice_number}</strong>
                </p>
              )}
            </div>
          </div>

          {/* Reported Issue */}
          <div className="p-3 bg-white rounded border border-brand-border">
            <p className="font-bold text-brand-black uppercase">Reported Claim Issue</p>
            <p className="text-brand-black mt-1 font-medium">{claim.issue}</p>
          </div>

          {/* Resolution */}
          {claim.resolution && (
            <div className="p-3 bg-green-50/60 rounded border border-green-200">
              <p className="font-bold text-green-900 uppercase">Claim Resolution / Outcome</p>
              <p className="text-green-800 mt-1 font-medium">{claim.resolution}</p>
            </div>
          )}

          {/* Inline Edit Status Form */}
          {isEditing && (
            <form onSubmit={handleSave} className="p-3 bg-white border-2 border-brand-green rounded-lg space-y-3">
              <h4 className="font-bold text-brand-black uppercase">Update Claim Workflow Status</h4>
              {updateError && <ErrorAlert message={updateError} />}

              <div>
                <label className="block font-bold uppercase mb-1">Claim Status</label>
                <select
                  value={status}
                  onChange={(e) => setStatus(e.target.value as WarrantyClaimStatus)}
                  className="w-full px-3 py-1.5 border rounded font-semibold text-brand-black"
                >
                  {WARRANTY_STATUSES.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block font-bold uppercase mb-1">Resolution Details / Actions Taken</label>
                <textarea
                  rows={2}
                  placeholder="e.g. Replaced SSD under warranty, tested ok..."
                  value={resolution}
                  onChange={(e) => setResolution(e.target.value)}
                  className="w-full px-3 py-1.5 border rounded font-medium resize-none"
                />
              </div>

              <div className="flex justify-end space-x-2 pt-1 border-t">
                <button
                  type="button"
                  onClick={() => setIsEditing(false)}
                  className="px-3 py-1 bg-brand-bg border font-semibold rounded"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={updateMutation.isPending}
                  className="inline-flex items-center space-x-1 px-4 py-1 bg-brand-green text-white font-bold rounded hover:bg-brand-green-hover"
                >
                  {updateMutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                  <span>Save Outcome</span>
                </button>
              </div>
            </form>
          )}
        </div>
      )}

      {/* Footer */}
      <div className="flex justify-end pt-3 border-t border-brand-border">
        <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-brand-muted hover:text-brand-black">
          Close
        </button>
      </div>
    </>
  );
};
